/**
 * The one bar chart the financial profile draws. Yearly turnover, quarterly
 * turnover and quarterly taxes are the same picture over different buckets, so
 * the Recharts wiring lives here once and callers pass a title, a colour and a
 * series they have already decided is worth drawing.
 *
 * It renders nothing on an empty series. A frame with axis labels and no bars
 * is worse than no frame: it reads as a broken page rather than as absent data.
 */
import type { LucideIcon } from "lucide-react";
import { Bar, BarChart, CartesianGrid, XAxis } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@wa/ui";
import { formatEurFull } from "../../../../lib/ariregister";
import type { SeriesPoint } from "./lib";

/** Recharts hands the tooltip euros; the shared formatter speaks cents. */
function eurTooltip(value: unknown) {
  return (
    <span className="font-mono font-medium tabular-nums text-foreground">
      {formatEurFull(Math.round(Number(value) * 100))}
    </span>
  );
}

export function MoneyBars({
  title,
  icon: Icon,
  points,
  seriesLabel,
  color = "var(--color-primary)",
  height = 150,
  maxBarSize = 72,
}: {
  title: string;
  icon?: LucideIcon;
  points: SeriesPoint[];
  /** Name of the measure, shown in the tooltip header. */
  seriesLabel: string;
  color?: string;
  height?: number;
  maxBarSize?: number;
}) {
  if (points.length === 0) return null;
  const config = { value: { label: seriesLabel, color } } satisfies ChartConfig;
  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        {Icon ? <Icon className="size-3.5" /> : null} {title}
      </div>
      <ChartContainer config={config} className="aspect-auto w-full" style={{ height }}>
        <BarChart accessibilityLayer data={points} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} />
          <ChartTooltip cursor={false} content={<ChartTooltipContent formatter={eurTooltip} />} />
          <Bar dataKey="value" fill="var(--color-value)" radius={[5, 5, 0, 0]} maxBarSize={maxBarSize} />
        </BarChart>
      </ChartContainer>
    </div>
  );
}
